import i18n, {
  FR_LANGUAGE,
  EN_LANGUAGE,
  DE_LANGUAGE,
  NL_LANGUAGE,
  FALLBACK_LANGUAGE,
  DEFAULT_LANGUAGE,
  DEFAULT_CURRENCY,
  datetimeFormats,
} from "@/core/composables/vue-i18n";
import useAccountStore from "@/apps/shared/organization/store/accountStore";
import { NumberFormatOptions } from "@intlify/core-base";

type Language = {
  browserLanguage: string;
  lang: string;
  shortName: string;
  name: string;
};

type Currency = {
  currency: string;
  name: string;
};

const languages: Language[] = [
  {
    browserLanguage: "fr",
    lang: FR_LANGUAGE,
    shortName: "Français",
    name: "Français (fr-FR)",
  },
  {
    browserLanguage: "en",
    lang: EN_LANGUAGE,
    shortName: "English",
    name: "English (en-GB)",
  },
  {
    browserLanguage: "nl",
    lang: NL_LANGUAGE,
    shortName: "Nederlands",
    name: "Nederlands, Vlaams (nl)",
  },
  {
    browserLanguage: "de",
    lang: DE_LANGUAGE,
    shortName: "Deutsch",
    name: "Deutsch, Deutschland (de)",
  },
];

const currencies: Currency[] = [
  {
    currency: "EUR",
    name: "Euro (€)",
  },
  {
    currency: "GBP",
    name: "Pound (£)",
  },
  {
    currency: "USD",
    name: "Dollar ($)",
  },
  {
    currency: "CHF",
    name: "Franc Suisse (₣)",
  },
];

const i18nService = {
  languages: languages,
  currencies: currencies,

  getLanguage(countryCode: string = FALLBACK_LANGUAGE): Language | undefined {
    if (countryCode.length === 2) {
      return this.languages.find(({ browserLanguage }: Language) => browserLanguage === countryCode);
    } else {
      return this.languages.find(({ lang }: Language) => lang === countryCode);
    }
  },

  setActiveLanguage(lang: string): void {
    const newLanguage = this.getLanguage(lang)?.lang ?? FALLBACK_LANGUAGE;
    document.documentElement.setAttribute("lang", newLanguage);
    i18n.global.locale.value = newLanguage;
    i18n.global.setDateTimeFormat(i18n.global.locale.value, datetimeFormats[newLanguage]);
  },

  setActiveLanguageToDefault(): void {
    this.setActiveLanguage(DEFAULT_LANGUAGE);
  },

  getActiveLanguage(): string {
    const { language } = useAccountStore();
    return language || DEFAULT_LANGUAGE;
  },

  setActiveCurrency(newCurrency: string): void {
    const newNumberFormat: { [key: string]: NumberFormatOptions } = {
      currency: {
        currencyDisplay: "symbol",
        style: "currency",
        currency: newCurrency,
      },
    };

    i18n.global.setNumberFormat(i18n.global.locale.value, newNumberFormat);
  },

  setActiveCurrencyToDefault(): void {
    this.setActiveCurrency(DEFAULT_CURRENCY);
  },

  getActiveCurrency(): string {
    const { currency } = useAccountStore();
    return currency || DEFAULT_CURRENCY;
  },

  getCurrencySymbol(): string | undefined {
    const formatter = new Intl.NumberFormat(i18n.global.locale.value, {
      style: "currency",
      currency: this.getActiveCurrency(),
    });

    const parts = formatter.formatToParts(99);
    return parts.find((part) => part.type === "currency")?.value;
  },
};

export default i18nService;
